/**
 * O que faz: Card visual de um agendamento no modo lista da agenda.
 * Layout em duas zonas: coluna de horário (leitura rápida estilo calendário)
 * e conteúdo (cliente, procedimento, metadados), com faixa de ações no rodapé.
 * Onde usa: CalendarPage (modos lista do dia, semana e mês).
 * Conexões: Recebe o agendamento e callbacks da CalendarPage; usa
 * AgendamentoStatusActions para as transições de status e o util
 * '@/utils/agendamentoStatus' para cores/rótulos.
 * Tabelas: Nenhuma (apenas apresentação; mutações sobem via callbacks).
 * Autor: IA (Kilo)
 * Data: 22/07/2026
 */
import { differenceInMinutes, format } from 'date-fns';
import { Timer, MessageSquare, Pencil, Trash2 } from 'lucide-react';
import { AgendamentoFromDatabase } from '@/hooks/useAgendamentosData';
import { AgendamentoStatus } from '@/constants/agendamentos';
import { AgendamentoStatusActions } from '@/components/agendamentos/AgendamentoStatusActions';
import { getStatusClasses, getStatusText } from '@/utils/agendamentoStatus';
import { rotuloFormaPagamento } from '@/constants/pagamentoAgendamento';
import { cn } from '@/lib/utils';

interface AgendamentoCardProps {
  agendamento: AgendamentoFromDatabase;
  clienteNome: string;
  onEdit: (agendamento: AgendamentoFromDatabase) => void;
  onDelete: (agendamentoId: string) => void;
  onOpenChat: (clienteId: string) => void;
  onStatusChange: (agendamento: AgendamentoFromDatabase, newStatus: AgendamentoStatus) => void;
}

// Gera as iniciais do cliente (até 2 letras) para o avatar.
// Fallback '?' evita avatar vazio quando o lead não tem nome cadastrado.
const obterIniciais = (nome: string): string => {
  const iniciais = nome
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(parte => parte.charAt(0).toUpperCase())
    .join('');
  return iniciais || '?';
};

// Converte a duração em texto curto ("45 min", "1h 30min").
// Retorna null quando data_fim <= data_inicio (dado inconsistente), para não exibir "0 min".
const formatarDuracao = (inicio: string, fim: string): string | null => {
  const minutos = differenceInMinutes(new Date(fim), new Date(inicio));
  if (minutos <= 0) return null;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (horas === 0) return `${resto} min`;
  return resto > 0 ? `${horas}h ${resto}min` : `${horas}h`;
};

// Apenas estes status possuem ações rápidas (ver AgendamentoStatusActions).
// Checar antes evita renderizar uma faixa de rodapé vazia no card.
const STATUS_COM_ACOES: AgendamentoStatus[] = [
  AgendamentoStatus.AGENDADO,
  AgendamentoStatus.CONFIRMADO,
  AgendamentoStatus.REALIZADO,
];

export const AgendamentoCard = ({
  agendamento,
  clienteNome,
  onEdit,
  onDelete,
  onOpenChat,
  onStatusChange,
}: AgendamentoCardProps) => {
  const classes = getStatusClasses(agendamento.status);
  const duracao = formatarDuracao(agendamento.data_inicio, agendamento.data_fim);
  const temAcoesDeStatus = STATUS_COM_ACOES.includes(agendamento.status as AgendamentoStatus);

  return (
    <div
      onClick={() => onEdit(agendamento)}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-card-hover',
        // Cancelado/faltou perde destaque visual: reduz ruído na lista sem esconder o registro.
        (agendamento.status === AgendamentoStatus.CANCELADO ||
          agendamento.status === AgendamentoStatus.NAO_COMPARECEU) &&
          'opacity-60 hover:opacity-100',
      )}
    >
      {/* Corpo: coluna de horário + conteúdo */}
      <div className="flex items-stretch gap-3.5 p-4">
        {/* Coluna de horário: leitura rápida no padrão de apps de calendário.
            A linha vertical liga visualmente início e fim do atendimento. */}
        <div className="flex w-[52px] flex-shrink-0 flex-col items-center rounded-xl border border-gray-100 bg-gray-50/80 py-2.5">
          <span className="text-[13px] font-bold leading-none text-gray-900">
            {format(new Date(agendamento.data_inicio), 'HH:mm')}
          </span>
          <span className="my-1.5 w-px flex-1 bg-gray-300" />
          <span className="text-[11px] font-medium leading-none text-gray-500">
            {format(new Date(agendamento.data_fim), 'HH:mm')}
          </span>
        </div>

        {/* Conteúdo principal */}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {/* Linha 1: avatar + cliente + badge de status */}
          <div className="flex items-start gap-2.5">
            <div
              className={cn(
                'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                classes.softBg,
                classes.tagText,
              )}
              aria-hidden
            >
              {obterIniciais(clienteNome)}
            </div>

            <div className="min-w-0 flex-1">
              <h4 className="truncate text-sm font-semibold leading-tight text-gray-900">
                {clienteNome}
              </h4>
              <p className="mt-0.5 truncate text-xs leading-tight text-gray-500">
                {agendamento.titulo}
              </p>
            </div>

            <span
              className={cn(
                'inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                classes.tagBg,
                classes.tagText,
              )}
            >
              <span className={cn('h-1.5 w-1.5 rounded-full', classes.dot)} />
              {getStatusText(agendamento.status)}
            </span>
          </div>

          {/* Linha 2: metadados (duração e valor) em chips discretos */}
          {(duracao || (agendamento.valor != null && agendamento.valor > 0)) && (
            <div className="flex flex-wrap items-center gap-1.5 pl-[46px]">
              {duracao && (
                <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                  <Timer size={11} className="text-gray-400" />
                  {duracao}
                </span>
              )}
              {agendamento.valor != null && agendamento.valor > 0 && (
                <span className="inline-flex items-center rounded-md bg-accent px-1.5 py-0.5 text-[11px] font-semibold text-accent-foreground">
                  {agendamento.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  {agendamento.status === AgendamentoStatus.PAGO && agendamento.forma_pagamento
                    ? ` · ${agendamento.forma_pagamento === 'cartao' && agendamento.parcelas && agendamento.parcelas > 1
                      ? `${agendamento.parcelas}x cartão`
                      : rotuloFormaPagamento(agendamento.forma_pagamento)}`
                    : ''}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Faixa de ações: fundo levemente tingido separa o conteúdo das ações.
          stopPropagation impede que cliques aqui abram o modal de edição. */}
      <div
        className="flex items-center justify-between gap-2 border-t border-gray-100 bg-gray-50/60 px-3 py-2"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex flex-wrap items-center gap-1.5">
          {temAcoesDeStatus && (
            <AgendamentoStatusActions
              agendamento={agendamento}
              onStatusChange={newStatus => onStatusChange(agendamento, newStatus)}
            />
          )}
        </div>

        {/* Ícones aparecem no hover no desktop (reduz poluição visual)
            e ficam sempre visíveis no mobile (não há hover). */}
        <div className="ml-auto flex items-center gap-0.5 transition-opacity md:opacity-0 md:group-hover:opacity-100">
          <button
            type="button"
            onClick={() => onOpenChat(agendamento.cliente_id)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
            title="Conversar com o cliente"
          >
            <MessageSquare size={14} />
          </button>
          <button
            type="button"
            onClick={() => onEdit(agendamento)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-200/70 hover:text-gray-700"
            title="Editar agendamento"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(agendamento.id)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
            title="Excluir agendamento"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};
